<template>
  <section>
    <AppPageTitleBar
      :title="route.meta.title"
      :back-to="inviteeListRoute"
      back-label="Natrag"
    />

    <InviteeForm
      v-if="isInviteeFormAvailable && (!isEditMode || inviteeToEdit)"
      :key="isEditMode ? inviteeId : inviteeFormKey"
      :form-id="isEditMode ? 'invitee-edit-form' : 'invitee-create-form'"
      :initial-invitee-values="inviteeToEdit"
      :submit-text="isEditMode ? 'Spremi' : 'Prihvati'"
      :is-saving-invitee="isSavingInvitee"
      :show-email-notice="!isEditMode"
      :show-save-and-add-another="!isEditMode"
      @submit="saveInviteeAsync"
    />
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AppPageTitleBar from '../../components/AppPageTitleBar.vue'
import InviteeForm from '../../components/InviteeForm.vue'
import { apiClient } from '../../services/apiClient'
import { getActivityEventAsync } from '../../services/offlineService'
import { showErrorToast, showRequestErrorToast, showSuccessToast } from '../../store/appStore'

const route = useRoute()
const router = useRouter()

const eventId = computed(() => route.params.eventId)
const inviteeId = computed(() => route.params.inviteeId)
const isEditMode = computed(() => Boolean(inviteeId.value))
const inviteeListRoute = computed(() => ({
  name: 'event-detail-invitees',
  params: { eventId: eventId.value },
}))
const inviteeToEdit = ref(null)
const isInviteeFormAvailable = ref(false)
const isSavingInvitee = ref(false)
const inviteeFormKey = ref(0)

watch([eventId, inviteeId], getInviteeAsync, { immediate: true })

async function getInviteeAsync () {
  inviteeToEdit.value = null
  isInviteeFormAvailable.value = false

  try {
    const event = await getActivityEventAsync(eventId.value)

    if (event.isArchived) {
      showErrorToast('Uzvanike arhiviranog događaja nije moguće uređivati.')
      await router.replace(inviteeListRoute.value)
      return
    }

    isInviteeFormAvailable.value = true

    if (!isEditMode.value) {
      return
    }

    const invitee = event.invitees.find(({ id }) => id === inviteeId.value)
    if (!invitee) {
      throw new Error('The invitee is not assigned to this event.')
    }

    inviteeToEdit.value = {
      ...invitee,
      name: invitee.fullName,
    }
  } catch (error) {
    console.error('Failed to load the event invitee.', error)
    showRequestErrorToast(error, 'Uzvanika nije bilo moguće učitati.')
  }
}

async function saveInviteeAsync (inviteeFormValues, saveAndAddAnother) {
  if (isSavingInvitee.value) {
    return
  }

  isSavingInvitee.value = true

  try {
    const { name, ...inviteeValues } = inviteeFormValues

    await apiClient.postAsync(`/api/events/${eventId.value}/invitees`, {
      ...(isEditMode.value ? { inviteeId: inviteeId.value } : {}),
      fullName: name,
      ...inviteeValues,
    })

    showSuccessToast(isEditMode.value
      ? 'Uzvanik je uspješno spremljen.'
      : 'Uzvanik je uspješno dodan.')

    if (!isEditMode.value && saveAndAddAnother) {
      inviteeFormKey.value += 1
      return
    }

    await router.push(inviteeListRoute.value)
  } catch (error) {
    console.error(`Failed to ${isEditMode.value ? 'save' : 'add'} the event invitee.`, error)
    showRequestErrorToast(
      error,
      isEditMode.value
        ? 'Uzvanika nije bilo moguće spremiti.'
        : 'Uzvanika nije bilo moguće dodati.'
    )
  } finally {
    isSavingInvitee.value = false
  }
}
</script>
